import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
    Canvas,
    canvas,
    circleMarker,
    CircleMarkerOptions,
    Control,
    DomUtil,
    geoJSON,
    GeoJSONOptions,
    LatLng,
    latLngBounds,
    Layer,
    map,
    Map,
    polygon,
    StyleFunction,
    tileLayer,
} from 'leaflet';
import html2canvas from 'html2canvas';
import { DownloadOptions, ZipOptions, zip } from '@mapbox/shp-write';
import { Geometry, GeoJSON, GeoJsonProperties, Feature, MultiPolygon } from 'geojson';
import { CommonModule, DatePipe } from '@angular/common';
import { saveAs } from 'file-saver';
import { HatchImage } from './map.constants';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbCollapseModule, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { MatSelectModule } from '@angular/material/select';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

export interface LegendConfig {
    title: string;
    values: { color: string; value: string; size?: number }[];
}

@UntilDestroy()
@Component({
    selector: 'ed-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: true,
    imports: [CommonModule, MatMenuModule],
    providers: [DatePipe],
})
export class MapComponent implements AfterViewInit, OnDestroy {
    @Input() layers: GeoJSON.FeatureCollection[] = [];
    @Input() getStyle: StyleFunction<GeoJsonProperties> = () => ({});
    @Input() getTooltip: (feature: Feature) => string = () => '';
    @Input() legendConfig: LegendConfig[] = [];
    @Input() title: string = '';
    @Input() mapId: string = 'mapId';

    public hatchImage = HatchImage;
    private _map!: Map;
    private _leafletLayers: GeoJSON<any, Geometry>[] = [];

    constructor(private readonly datePipe: DatePipe) {}

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this._plugin();
            this._initializeMap();
            this._addLayers();
            this._centerMap();
            this._addLegend();
        }, 0);
    }

    private _plugin(): void {
        // Plugin code to enable image patterns on canvas element : https://github.com/bgx1012/leaflet-polygon-fillPattern-canvas
        if (Canvas) {
            Canvas.include({
                _fillStroke: function (ctx: any, layer: any) {
                    const options = layer.options;

                    if (options.fill) {
                        ctx.globalAlpha = options.fillOpacity;
                        ctx.fillStyle = options.fillColor || options.color;

                        ctx.fill(options.fillRule || 'evenodd');
                    }

                    if (options.stroke && options.weight !== 0) {
                        if (ctx.setLineDash) {
                            ctx.setLineDash((layer.options && layer.options._dashArray) || []);
                        }

                        ctx.globalAlpha = options.opacity;
                        ctx.lineWidth = options.weight;
                        ctx.strokeStyle = options.color;
                        ctx.lineCap = options.lineCap;
                        ctx.lineJoin = options.lineJoin;
                        ctx.stroke();
                        if (options.imgId) {
                            var img = document.getElementById(options.imgId);
                            ctx.save(); // so we can remove the clipping
                            ctx.clip();
                            var bounds = layer._rawPxBounds;
                            var size = bounds.getSize();
                            var pattern = ctx.createPattern(img, 'repeat');
                            ctx.fillStyle = pattern;
                            ctx.fillRect(bounds.min.x, bounds.min.y, size.x, size.y);
                            ctx.restore();
                        }
                    }
                },
            });
        }
    }

    private _initializeMap(): void {
        this._leafletLayers = [];
        const baseMapURl = 'https://a.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png';
        this._map = map(this.mapId, {
            renderer: canvas(), // Rendre toutes les couches avec Canvas
            scrollWheelZoom: false,
        });
        tileLayer(baseMapURl, {
            crossOrigin: true, // Important pour html2canvas
        }).addTo(this._map);
        this._map.attributionControl.setPrefix('Leaflet');
    }

    private _addLayers(): void {
        this.layers.forEach((layer) => {
            const options: GeoJSONOptions = {
                style: this.getStyle,
                onEachFeature: this._onEachFeature.bind(this),
            };

            const isPointsLayer = layer.features[0]?.geometry?.type === 'Point';

            if (isPointsLayer) {
                options.pointToLayer = this._pointsToCircles();
            }

            // Hatch all the cities out of concession
            layer.features.forEach((feature) => {
                if (feature?.properties && feature.properties['isInConcession'] === false) {
                    const coordinates = (feature.geometry as MultiPolygon).coordinates.map(
                        (polygon) =>
                            polygon.map((pointsArray) =>
                                pointsArray.map((point) => {
                                    return { lat: point[1], lng: point[0] };
                                }),
                            ),
                    );
                    const hatchedPolygon = polygon(coordinates, {
                        imgId: 'hatch',
                        color: 'black',
                        weight: 1,
                    } as any);
                    hatchedPolygon.addTo(this._map);
                    this._leafletLayers.push(hatchedPolygon);
                }
            });

            const leafletLayer = geoJSON(layer, options);
            leafletLayer.addTo(this._map);
            this._leafletLayers.push(leafletLayer);
        });
    }

    private _pointsToCircles() {
        return (feature: GeoJSON.Feature, latlng: LatLng) => {
            const style = this.getStyle(feature) as CircleMarkerOptions;
            return circleMarker(latlng, style);
        };
    }

    private _centerMap(): void {
        // Create a LatLngBounds object to encompass all the features locations
        const bounds = latLngBounds(this._leafletLayers.map((layer) => layer.getBounds()));

        // Fit the map view to the bounds
        this._map.fitBounds(bounds);
        window.dispatchEvent(new Event('resize'));
    }

    private _onEachFeature(feature: GeoJSON.Feature, layer: Layer): void {
        layer.bindTooltip(this.getTooltip(feature), {
            permanent: !!feature.properties!['showTooltip'],
        });
    }

    private _addLegend() {
        const legend = new Control({ position: 'bottomleft' });

        legend.onAdd = () => {
            const div = DomUtil.create('div', 'legend');

            this.legendConfig.forEach((config) => {
                div.innerHTML += `<div class="legend-title">${config.title}</div>`;
                const maxSize = config.values.reduce(
                    (acc, value) => ((value.size || 0) > acc ? value.size || 0 : acc),
                    0,
                );
                config.values.forEach((value) => {
                    let style = `background:${value.color};`;

                    if (value.size !== undefined) {
                        style += `border-radius: 50%;width: ${value.size * 2}px;height: ${value.size * 2}px;margin: 0 ${maxSize - value.size}px;`;

                        if (value.size === 0) {
                            style += 'border: none';
                        }
                    }

                    div.innerHTML += `<div class="legend-item"><i style="${style}"></i>${value.value}</div>`;
                });
            });

            return div;
        };

        legend.addTo(this._map);

        setTimeout(() => {
            this._map.invalidateSize({});
        }, 100);
    }

    public export(layerOrder?: number): void {
        const date = new Date();
        const fileName = `${this.title.replace(' ', '_')}_${this.datePipe.transform(date, "dd.MM.yyyy_HH'h'mm")}`;

        if (layerOrder !== undefined) {
            this.downloadShapeFile(layerOrder, fileName);
        } else {
            this.downloadImage(fileName);
        }
    }

    public async downloadImage(fileName: string): Promise<void> {
        const mapElement = document.getElementById(this.mapId);

        if (!mapElement) {
            return;
        }

        this._leafletLayers.forEach((l) => l.bringToFront());

        const canvas = await html2canvas(mapElement, {
            useCORS: true, // Permet de capturer les tuiles externes respectant CORS.
            logging: true, // Utile pour le débogage.
            allowTaint: false, // Empêche les erreurs liées au CORS
        });
        // Convertir le canvas en image
        const imgData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `${fileName}.png`;
        link.href = imgData;
        link.click();
    }

    public async downloadShapeFile(layerOrder: number, fileName: string): Promise<void> {
        const options: DownloadOptions & ZipOptions = {
            folder: fileName,
            filename: this.legendConfig[layerOrder].title,
            outputType: 'blob',
            compression: 'DEFLATE',
        };
        const zipFile = await zip(this.layers[layerOrder], options);
        saveAs(zipFile as Blob, `${fileName}.zip`);
    }

    public ngOnDestroy(): void {
        this._map.remove();
    }
}
